import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, FormControlLabel, Grid, Paper, Switch, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import Layout from '../components/Layout';
import { settingsAPI, stockAPI } from '../services/api';
import { definirTableFer } from '../utils/fer';
import { apiError } from '../utils/format';

const GROUPS = [
  ['Société (en-tête des factures)', [
    ['raison_sociale', 'Raison sociale'], ['adresse', 'Adresse'], ['telephone', 'Téléphone'], ['email', 'Email'],
    ['rccm', 'RCCM'], ['ninea', 'NINEA / N° TVA'], ['banque', 'Banque'], ['iban', 'IBAN'],
  ]],
  ['Paie (en-tête des bulletins de salaire)', [
    ['ipres_employeur', 'N° employeur IPRES'], ['css_employeur', 'N° employeur CSS'],
  ]],
  ['En-tête des bons de livraison', [
    ['activite', 'Activité'], ['bl_adresse', 'Adresse (BL)'], ['bl_telephone', 'Téléphone (BL)'], ['capital_social', 'Capital social'],
  ]],
];

const Parametres = () => {
  const [s, setS] = useState(null);
  const [types, setTypes] = useState([]);
  const [numbering, setNumbering] = useState({});
  const [testTo, setTestTo] = useState('');
  const [mail, setMail] = useState({ smtp_host: '', smtp_port: 465, smtp_user: '', smtp_pass: '', smtp_from: '', app_url: '' });
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [matu, setMatu] = useState({ jours: '', appliquer: false });
  const [fer, setFer] = useState(null);
  const [test, setTest] = useState(null); // { envoi } | { ok, texte }

  const load = () => {
    settingsAPI.get().then((r) => {
      setS({ ...r.data, cachet_image: undefined, cachet_actuel: r.data.cachet_image });
      setNumbering({ prochaine_facture: r.data.prochaine_facture, prochain_bl: r.data.prochain_bl });
      setMatu({ jours: r.data.maturation_jours, appliquer: false });
      setMail({ smtp_host: r.data.smtp_host || '', smtp_port: r.data.smtp_port || 465, smtp_user: r.data.smtp_user || '', smtp_pass: '',
        smtp_from: r.data.smtp_from || '', app_url: r.data.app_url || '' });
    }).catch((err) => setError(apiError(err)));
    stockAPI.getPbaTypes().then((r) => setTypes(r.data));
    settingsAPI.fer().then((r) => setFer(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const envoyerTest = async () => {
    setTest({ envoi: true });
    try { const r = await settingsAPI.emailTest(testTo.trim()); setTest({ ok: true, texte: r.data.message }); } catch (err) { setTest({ ok: false, texte: apiError(err) }); }
  };

  const notify = (fn) => async () => {
    setError(''); setMessage('');
    try { const r = await fn(); setMessage(r.data.message || 'Enregistré'); load(); } catch (err) { setError(apiError(err)); }
  };

  const onStamp = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 400 * 1024) { setError('Image trop lourde (400 Ko maximum)'); return; }
    const reader = new FileReader();
    reader.onload = () => setS((p) => ({ ...p, cachet_image: reader.result, cachet_actuel: reader.result, afficher_cachet: true }));
    reader.readAsDataURL(file);
  };

  if (!s) return <Layout title="Paramètres société">{error ? <Alert severity="error">{error}</Alert> : 'Chargement…'}</Layout>;
  const field = (k, label, props = {}) => (
    <TextField key={k} fullWidth size="small" sx={{ mb: 2 }} label={label} value={s[k] ?? ''}
      onChange={(e) => setS({ ...s, [k]: e.target.value })} {...props} />
  );

  return (
    <Layout title="Paramètres société">
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMessage('')}>{message}</Alert>}

      <Grid container spacing={3}>
        {GROUPS.map(([title, fields]) => (
          <Grid item xs={12} md={6} key={title}>
            <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
              <Typography variant="h6" gutterBottom>{title}</Typography>
              {fields.map(([k, l]) => field(k, l))}
              {title.startsWith('En-tête des bons') && field('bl_mentions', 'Mentions bas de BL (homologation)', { multiline: true, minRows: 2 })}
              {title.startsWith('En-tête des bons') && field('url_verification', 'Adresse du lien de vérification (QR code)', {
                placeholder: window.location.origin,
                helperText: `Laisser vide pour utiliser l'adresse de l'application (${window.location.origin}). Le QR ne fonctionne que si cette adresse est joignable par celui qui le scanne.`,
              })}
            </Paper>
          </Grid>
        ))}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Facturation</Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              {field('prefixe_facture', 'Préfixe (FA)')}
              {field('taux_tva', 'TVA %', { type: 'number' })}
              {field('delai_paiement_jours', 'Délai de paiement (jours)', { type: 'number' })}
            </Box>
            {field('mentions', 'Mentions en bas de facture (facultatif)', { multiline: true, minRows: 2 })}
            <Typography variant="subtitle2">Cachet (facultatif)</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Image du cachet apposée sur les factures émises. Ne mettez une signature que si vous acceptez qu'elle figure sur toute facture émise depuis l'application.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
              <Button variant="outlined" component="label">Choisir une image<input hidden type="file" accept="image/png,image/jpeg,image/webp" onChange={onStamp} /></Button>
              {s.cachet_actuel && <img src={s.cachet_actuel} alt="Cachet" style={{ maxHeight: 60, maxWidth: 160 }} />}
              {s.cachet_actuel && <Button color="error" onClick={() => setS({ ...s, cachet_image: null, cachet_actuel: null, afficher_cachet: false })}>Retirer</Button>}
            </Box>
            <FormControlLabel control={<Switch checked={!!s.afficher_cachet} disabled={!s.cachet_actuel}
              onChange={(e) => setS({ ...s, afficher_cachet: e.target.checked })} />} label="Afficher le cachet sur les factures" />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Numérotation {s.annee}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Les numéros sont continus et jamais réutilisés. Vous pouvez seulement avancer le prochain numéro.
            </Typography>
            <TextField fullWidth size="small" type="number" sx={{ mb: 2 }}
              label={`Prochaine facture (${s.prefixe_facture}-${s.annee}-${String(numbering.prochaine_facture || '').padStart(3, '0')})`}
              value={numbering.prochaine_facture || ''} onChange={(e) => setNumbering({ ...numbering, prochaine_facture: e.target.value })} />
            <TextField fullWidth size="small" type="number" sx={{ mb: 2 }}
              label={`Prochain BL (BL-${s.annee}-${String(numbering.prochain_bl || '').padStart(6, '0')})`}
              value={numbering.prochain_bl || ''} onChange={(e) => setNumbering({ ...numbering, prochain_bl: e.target.value })} />
            <Button variant="outlined" onClick={notify(() => settingsAPI.updateNumbering({
              prochaine_facture: parseInt(numbering.prochaine_facture, 10), prochain_bl: parseInt(numbering.prochain_bl, 10),
            }))}>Mettre à jour la numérotation</Button>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Production</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Délai entre la production d'un poteau et le moment où il peut être livré. Actuellement : <b>{s.maturation_jours} jours</b>.
            </Typography>
            <TextField fullWidth size="small" type="number" sx={{ mb: 1 }} label="Durée de maturation (jours)" inputProps={{ min: 1, max: 90 }}
              value={matu.jours} onChange={(e) => setMatu({ ...matu, jours: e.target.value })} />
            <FormControlLabel sx={{ mb: 1 }} control={<Switch checked={matu.appliquer} onChange={(e) => setMatu({ ...matu, appliquer: e.target.checked })} />}
              label="Appliquer aussi aux lots encore en maturation" />
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sinon, seuls les prochains rapports journaliers utilisent la nouvelle durée. Les dates saisies à la main et les lots déjà entamés par une livraison ne changent pas.
            </Typography>
            <Button variant="outlined" disabled={String(matu.jours) === String(s.maturation_jours) && !matu.appliquer}
              onClick={notify(() => settingsAPI.updateMaturation({ jours: parseInt(matu.jours, 10), appliquer_lots: matu.appliquer }))}>Enregistrer la durée</Button>
            {fer && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Poids d'une barre de fer (kg)</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Sert à convertir les barres en kg : rapport journalier, stock intrants, bons de commande et synthèses. Case vide : barres non converties.
                </Typography>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Fer</TableCell><TableCell align="right">kg par barre</TableCell><TableCell align="right">Barres / 100 kg</TableCell></TableRow></TableHead>
                  <TableBody>
                    {fer.variantes.map(({ d, o }) => {
                      const v = fer.table[d]?.[o] ?? '';
                      const lib = `Fer de ${d} ${o === 'LOCAL' ? 'local' : 'importé'}`;
                      return (
                        <TableRow key={`${d}${o}`}>
                          <TableCell sx={{ fontWeight: 600 }}>{lib}</TableCell>
                          <TableCell align="right">
                            <TextField variant="standard" sx={{ width: 70 }} value={v} placeholder="—"
                              inputProps={{ inputMode: 'decimal', style: { textAlign: 'right' }, 'aria-label': `${lib} kg par barre` }}
                              onChange={(e) => { const x = e.target.value.replace(',', '.').replace(/[^\d.]/g, ''); setFer({ ...fer, table: { ...fer.table, [d]: { ...fer.table[d], [o]: x } } }); }} />
                          </TableCell>
                          <TableCell align="right" sx={{ color: 'text.secondary', fontSize: 13 }}>{Number(v) > 0 ? Math.round(100 / Number(v)) : '—'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                <Button variant="outlined" sx={{ mt: 1.5 }} onClick={notify(async () => { const r = await settingsAPI.updateFer(fer.table); definirTableFer(r.data.table); setFer({ ...fer, table: r.data.table }); return r; })}>
                  Enregistrer les poids
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Emails (alertes de validation)</Typography>
            {s.email_configure ? (
              <Alert severity="success" sx={{ mb: 2 }}>
                Envoi configuré{s.email_source === 'env' ? ' (fichier .env du serveur)' : ''} — expéditeur : {s.email_expediteur}
              </Alert>
            ) : (
              <Alert severity="warning" sx={{ mb: 2 }}>Envoi non configuré : les valideurs ne reçoivent pas d'alerte. Renseignez le compte d'envoi ci-dessous.</Alert>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Compte de messagerie utilisé pour envoyer les alertes. Zoho Mail : serveur <b>smtppro.zoho.com</b>, port <b>465</b>,
              et un <b>mot de passe d'application</b> (Zoho → Mon compte → Sécurité → Mots de passe d'application).
            </Typography>
            <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
              <Grid item xs={8}><TextField fullWidth size="small" label="Serveur SMTP" placeholder="smtppro.zoho.com" value={mail.smtp_host} onChange={(e) => setMail({ ...mail, smtp_host: e.target.value })} /></Grid>
              <Grid item xs={4}><TextField fullWidth size="small" label="Port" type="number" value={mail.smtp_port} onChange={(e) => setMail({ ...mail, smtp_port: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth size="small" label="Adresse d'envoi (identifiant)" placeholder="erp@docc-prefa.com" value={mail.smtp_user} onChange={(e) => setMail({ ...mail, smtp_user: e.target.value })} /></Grid>
              <Grid item xs={12}>
                <TextField fullWidth size="small" label="Mot de passe d'application" type="password" autoComplete="new-password" value={mail.smtp_pass}
                  placeholder={s.smtp_pass_defini ? 'Enregistré — laisser vide pour le garder' : ''}
                  helperText={s.smtp_pass_defini ? 'Un mot de passe est enregistré (chiffré). Saisissez-en un nouveau seulement pour le changer.' : 'Stocké chiffré, jamais réaffiché.'}
                  onChange={(e) => setMail({ ...mail, smtp_pass: e.target.value })} />
              </Grid>
              <Grid item xs={12}><TextField fullWidth size="small" label="Nom affiché (facultatif)" placeholder={`DOCC ERP <${mail.smtp_user || 'erp@docc-prefa.com'}>`} value={mail.smtp_from} onChange={(e) => setMail({ ...mail, smtp_from: e.target.value })} /></Grid>
              <Grid item xs={12}><TextField fullWidth size="small" label="Adresse de l'ERP dans les liens" placeholder={window.location.origin} value={mail.app_url}
                helperText={`Utilisée dans le bouton « Ouvrir et valider ». Actuellement : ${s.email_app_url || 'non définie'}`}
                onChange={(e) => setMail({ ...mail, app_url: e.target.value })} /></Grid>
            </Grid>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button variant="contained" onClick={notify(() => settingsAPI.saveEmail({ ...mail, smtp_port: parseInt(mail.smtp_port, 10) || 465 }))}>Enregistrer la configuration email</Button>
              {s.smtp_host && <Button color="error" onClick={() => window.confirm('Effacer la configuration email ?') && notify(() => settingsAPI.saveEmail({ effacer: true }))()}>Effacer</Button>}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField size="small" fullWidth label="Envoyer un email de test à" value={testTo} onChange={(e) => setTestTo(e.target.value)} />
              <Button variant="outlined" disabled={!testTo.trim() || !!test?.envoi} onClick={envoyerTest} sx={{ whiteSpace: 'nowrap' }}>{test?.envoi ? 'Envoi…' : 'Envoyer'}</Button>
            </Box>
            {test?.envoi && <Alert severity="info" sx={{ mt: 1.5 }}>Connexion au serveur {mail.smtp_host || s.smtp_host || 'SMTP'}… (jusqu'à 30 secondes si la connexion est bloquée)</Alert>}
            {test && !test.envoi && <Alert severity={test.ok ? 'success' : 'error'} sx={{ mt: 1.5, wordBreak: 'break-word' }} onClose={() => setTest(null)}>{test.texte}</Alert>}
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ my: 3, textAlign: 'right' }}>
        <Button variant="contained" size="large" onClick={notify(() => {
          const { cachet_actuel, annee, prochaine_facture, prochain_bl, email_configure, email_expediteur, smtp_host, smtp_port, smtp_user, smtp_from, app_url, smtp_pass_defini, email_source, email_app_url, ...rest } = s; // eslint-disable-line
          return settingsAPI.update({ ...rest, taux_tva: Number(rest.taux_tva), delai_paiement_jours: parseInt(rest.delai_paiement_jours, 10) });
        })}>Enregistrer les paramètres</Button>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Catalogue des poteaux : désignations et prix de vente HT</Typography>
        <Table size="small">
          <TableHead>
            <TableRow><TableCell>Code</TableCell><TableCell>Désignation facture</TableCell><TableCell>Désignation BL (type homologué)</TableCell><TableCell align="right">Prix HT (FCFA)</TableCell><TableCell /></TableRow>
          </TableHead>
          <TableBody>
            {types.map((t, i) => (
              <TableRow key={t.id}>
                <TableCell><strong>{t.code}</strong></TableCell>
                <TableCell><TextField variant="standard" fullWidth value={t.designation || ''} onChange={(e) => setTypes(types.map((x, j) => (j === i ? { ...x, designation: e.target.value } : x)))} /></TableCell>
                <TableCell><TextField variant="standard" fullWidth value={t.designation_bl || ''} onChange={(e) => setTypes(types.map((x, j) => (j === i ? { ...x, designation_bl: e.target.value } : x)))} /></TableCell>
                <TableCell><TextField variant="standard" type="number" inputProps={{ style: { textAlign: 'right' } }} value={t.prix_ht} onChange={(e) => setTypes(types.map((x, j) => (j === i ? { ...x, prix_ht: e.target.value } : x)))} /></TableCell>
                <TableCell><Button size="small" onClick={notify(() => settingsAPI.updatePbaType(t.id, { ...t, prix_ht: parseInt(t.prix_ht, 10) || 0 }))}>Enregistrer</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </Layout>
  );
};

export default Parametres;
